import { useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";

import type { AdminDashUser } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import {
  adminDashImportStatusSchema,
  adminDashMembersSchema,
  adminDashUserSchema,
  RoleID,
} from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import type { CommunityMembersSchema } from "../../../../lib/types/schema/community/members/communityMembersSchema";
import { getRequestNextPageParam } from "../../../../lib/utils/getRequestNextPageParam";
import type { UseRequestInfiniteQueryProps } from "../../../../request/hooks/useRequestInfiniteQuery";
import { useRequestInfiniteQuery } from "../../../../request/hooks/useRequestInfiniteQuery";
import { useRequestMutation } from "../../../../request/hooks/useRequestMutation";
import { keyAdminDashUsers } from "./keyAdminDashUsers";

export type UseRequestAdminDashboardMembersProps =
  UseRequestInfiniteQueryProps & {
    id: number;
    filter?: "teacher" | "student";
    filter_by_is_admin?: "admin" | "member";
    search?: string;
  };

export const useRequestAdminDashboardMembers = ({
  id,
  filter,
  filter_by_is_admin,
  search,
  ...props
}: UseRequestAdminDashboardMembersProps) => {
  const queryClient = useQueryClient();
  const roleId = filter === "teacher" ? RoleID.Teacher : RoleID.Student;

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: [keyAdminDashUsers({ id, filter, filter_by_is_admin, search })],
    });
  };

  return {
    infiniteQuery: useRequestInfiniteQuery<typeof adminDashMembersSchema>({
      getNextPageParam: (
        lastPage: CommunityMembersSchema,
        _allPages: CommunityMembersSchema[],
        lastPageParam: number,
      ) =>
        getRequestNextPageParam({
          total: lastPage.total,
          lastPageParam,
          pageSize: 50,
        }),
      initialPageParam: 0,
      queryFnRequestProps: ({ pageParam }) => ({
        schema: adminDashMembersSchema,
        urlBackendPath: `/communities/${id}/admin/users?page=${pageParam}&search=${search}${filter ? `&filter_by_role_id=${roleId}` : ""}${filter_by_is_admin ? `&filter_by_is_admin=${filter_by_is_admin}` : ""}`,
      }),
      enabledWithoutSessionToken: false,
      queryKey: keyAdminDashUsers({ id, filter, filter_by_is_admin, search }),
      ...props,
    }),
    invalidateQueries,
    mutationPost: useRequestMutation<
      typeof adminDashUserSchema,
      Partial<AdminDashUser>
    >({
      mutationFnRequestProps: (props) => ({
        schema: adminDashUserSchema,
        urlBackendPath: `/communities/${id}/admin/users`,
        method: "POST",
        body: JSON.stringify(props),
      }),
      onSuccess: async () => await invalidateQueries(),
    }),
    mutationDelete: useRequestMutation<z.ZodString, { user_id: number }>({
      mutationFnRequestProps: ({ user_id }) => ({
        urlBackendPath: `/communities/${id}/members/${user_id}`,
        method: "DELETE",
      }),
      onSuccess: async () => await invalidateQueries(),
    }),
    mutationPostMultiple: useRequestMutation<
      z.ZodString,
      { user_ids: number[] }
    >({
      mutationFnRequestProps: (data) => ({
        urlBackendPath: `/communities/${id}/admin/members/multiple`,
        method: "POST",
        body: JSON.stringify(data),
      }),
      onSuccess: async () => await invalidateQueries(),
    }),
    mutationImport: useRequestMutation<z.ZodString, AdminDashUser[]>({
      mutationFnRequestProps: (users) => ({
        urlBackendPath: `/communities/${id}/admin/import`,
        method: "POST",
        body: JSON.stringify({
          users,
          role_id: roleId,
        }),
        raw: true,
      }),
    }),
    mutationGet: useRequestMutation<typeof adminDashImportStatusSchema, string>(
      {
        mutationFnRequestProps: (uuid) => ({
          urlBackendPath: `/communities/${id}/admin/import/${uuid}`,
          method: "GET",
          schema: adminDashImportStatusSchema,
        }),
      },
    ),
  };
};
